import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';
import withTranslations from '@wheelbox/lithejs-i18n';
import {
  BoxIcon,
  RouteIcon,
  DatabaseIcon,
  GlobeIcon,
  PaletteIcon,
  ImageIcon,
  FileTextIcon,
  ExternalLinkIcon,
} from '@wheelbox/lithejs-lucide';

import { SectionHeading } from '@components/shared';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@wheelbox/lithejs-ui';

export interface EcosystemSectionProps {
  id?: string;
  testId?: string;
}

const packageIcons: Record<string, typeof BoxIcon> = {
  engine: BoxIcon,
  router: RouteIcon,
  state: DatabaseIcon,
  i18n: GlobeIcon,
  ui: PaletteIcon,
  lucide: ImageIcon,
  markdown: FileTextIcon,
};

const packageLinks: Record<string, string> = {
  engine: 'https://www.npmjs.com/package/@wheelbox/lithejs-engine',
  router: 'https://www.npmjs.com/package/@wheelbox/lithejs-router',
  state: 'https://www.npmjs.com/package/@wheelbox/lithejs-state',
  i18n: 'https://www.npmjs.com/package/@wheelbox/lithejs-i18n',
  ui: 'https://www.npmjs.com/package/@wheelbox/lithejs-ui',
  lucide: 'https://www.npmjs.com/package/@wheelbox/lithejs-lucide',
  markdown: 'https://www.npmjs.com/package/@wheelbox/lithejs-markdown',
};

const EcosystemSection: LitheFC<EcosystemSectionProps> = ({
  id = 'ecosystem-section',
  testId = 'ecosystem-section',
}): LitheElement<'section'> => {
  const t = withTranslations('Home');

  const packages = [
    'engine',
    'router',
    'state',
    'i18n',
    'ui',
    'lucide',
    'markdown',
  ] as const;

  return [
    'section',
    {
      id,
      'data-testid': testId,
      class: 'py-20 px-4 bg-muted/50',
    },
    [
      'div',
      { class: 'max-w-6xl mx-auto' },
      SectionHeading({
        id: `${id}-heading`,
        title: t('ecosystem.title'),
        subtitle: t('ecosystem.subtitle'),
      }),
      [
        'div',
        {
          class:
            'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
        },
        ...packages.map((pkg, index) => {
          const Icon = packageIcons[pkg];
          return [
            'a',
            {
              href: packageLinks[pkg],
              target: '_blank',
              rel: 'noopener noreferrer',
              class: 'block group',
            },
            Card(
              {
                id: `${id}-pkg-${index}`,
                classes:
                  'h-full hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer',
              },
              CardHeader(
                {},
                [
                  'div',
                  { class: 'flex items-center justify-between mb-2' },
                  [
                    'div',
                    { class: 'p-2 rounded-lg bg-primary/10' },
                    Icon({ size: 20, classes: 'text-primary' }),
                  ],
                  ExternalLinkIcon({
                    size: 16,
                    classes:
                      'text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity',
                  }),
                ],
                CardTitle(
                  { classes: 'text-sm font-mono' },
                  t(`ecosystem.packages.${pkg}.name`)
                ),
                CardDescription(
                  { classes: 'text-xs mt-1' },
                  t(`ecosystem.packages.${pkg}.description`)
                )
              )
            ),
          ] as LitheElement<'a'>;
        }),
      ],
    ],
  ];
};

export default EcosystemSection;
